import {
  ChevronLeftIcon,
  ChevronRightIcon,
  MoreHorizontalIcon,
} from 'lucide-react';

import type { ComponentProps, JSX } from 'react';

import { cn } from '~/utils';
import { Button, buttonVariants } from '~/components/button';

export function Pagination({
  className,
  ...props
}: ComponentProps<'nav'>): JSX.Element {
  return (
    <nav
      role='navigation'
      aria-label='pagination'
      data-slot='pagination'
      className={cn('mx-auto flex w-full justify-center', className)}
      {...props}
    />
  );
}

export function PaginationContent({
  className,
  ...props
}: ComponentProps<'ul'>): JSX.Element {
  return (
    <ul
      data-slot='pagination-content'
      className={cn('flex flex-row items-center gap-1', className)}
      {...props}
    />
  );
}

export function PaginationItem(props: ComponentProps<'li'>): JSX.Element {
  return <li data-slot='pagination-item' {...props} />;
}

interface IPaginationLinkProps
  extends Pick<ComponentProps<typeof Button>, 'size'>,
    ComponentProps<'a'> {
  btnTitle?: string;
  isActive?: boolean;
  hideIcon?: boolean;
  disabled?: boolean;
}

export function PaginationLink({
  isActive,
  className,
  size = 'icon',
  ...props
}: IPaginationLinkProps): JSX.Element {
  return (
    <a
      data-active={isActive}
      data-slot='pagination-link'
      aria-current={isActive ? 'page' : undefined}
      className={cn(
        buttonVariants({
          size,
          variant: isActive ? 'outline' : 'ghost',
        }),
        className,
      )}
      {...props}
    />
  );
}

export function PaginationPrevious({
  className,
  hideIcon = false,
  btnTitle = 'Previous',
  ...props
}: ComponentProps<typeof PaginationLink>): JSX.Element {
  return (
    <PaginationLink
      size='default'
      aria-label='Go to previous page'
      className={cn('gap-1 px-2.5', className)}
      {...props}
    >
      {!hideIcon && <ChevronLeftIcon />}
      <span className='hidden sm:block'>{btnTitle}</span>
    </PaginationLink>
  );
}

export function PaginationNext({
  className,
  hideIcon = false,
  btnTitle = 'Next',
  disabled = false,
  ...props
}: ComponentProps<typeof PaginationLink>): JSX.Element {
  return (
    <PaginationLink
      size='default'
      disabled={disabled}
      aria-label='Go to next page'
      className={cn('gap-1 px-2.5', className)}
      {...props}
    >
      <span className='hidden sm:block'>{btnTitle}</span>
      {!hideIcon && <ChevronRightIcon />}
    </PaginationLink>
  );
}

export function PaginationEllipsis({
  className,
  ...props
}: ComponentProps<'span'>): JSX.Element {
  return (
    <span
      aria-hidden
      data-slot='pagination-ellipsis'
      className={cn('flex size-9 items-center justify-center', className)}
      {...props}
    >
      <MoreHorizontalIcon className='size-4' />
      <span className='sr-only'>More pages</span>
    </span>
  );
}
